/**
 * L'assistente alla trasparenza: orchestratore del widget.
 *
 * Sa tre cose e le tiene insieme: **dove** sta l'operatore (la rotta e il nodo
 * selezionato in Pubblicazioni), **che cosa** si e' detto finora (la storia in
 * `sessionStorage`) e **come** parlare con l'API (apertura dalla scheda, chat
 * in streaming). Tutto il resto e' presentazione, delegata a Launcher, Pannello,
 * Messaggi e Input.
 *
 * Il launcher compare solo se l'API dice che l'assistente e' acceso: in una
 * installazione senza Azure non c'e' nulla da mostrare.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { aperturaAssistente, ErroreApi, ErroreFiltro, statoAssistente, streamAssistente } from "../api/client";
import type { AperturaAssistente, Nodo } from "../api/types";
import { idNodoDallaRotta } from "./nodo-corrente";
import { useAutenticazione } from "../auth/contesto";
import { useAlberatura } from "../dati/contesto-alberatura";
import { prossimoPasso } from "./analisi-markdown";
import "./assistente.css";
import { Input } from "./Input";
import { Launcher } from "./Launcher";
import { Messaggi } from "./Messaggi";
import { Pannello } from "./Pannello";
import { leggiEventiSse } from "./sse";
import { rimedioAlBlocco } from "./blocco-filtro";
import { creaStoria, ultimiTurni, type Voce } from "./storia";

const RITARDO_LAUNCHER_MS = 700;
const TURNI_STORIA = 6;
const MESSAGGIO_ERRORE = "L'assistente non ha risposto, riprova.";
const MESSAGGIO_INCOMPLETA = "La risposta si è interrotta prima della fine, riprova.";
const SUGGERIMENTI_GENERALI = [
  "Una sottosezione può restare vuota?",
  "Per quanto tempo restano pubblicati i dati?",
  "Cosa significa aggiornamento tempestivo?",
];

function useMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 40rem)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 40rem)");
    const aggiorna = () => setMobile(mq.matches);
    mq.addEventListener("change", aggiorna);
    return () => mq.removeEventListener("change", aggiorna);
  }, []);
  return mobile;
}

function memoriaSessione(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Solo i messaggi che abbiamo scritto noi arrivano all'operatore; il resto e' generico. */
function messaggioPerOperatore(e: unknown): string {
  return e instanceof ErroreApi ? e.message : e instanceof ErroreInterna ? e.message : MESSAGGIO_ERRORE;
}

/** Un errore con un testo gia' pensato per l'operatore. */
class ErroreInterna extends Error {}

/** Decide se montare il widget e con quale chiave di storia: utente ed ente. */
export function Assistente() {
  const { utente } = useAutenticazione();
  const { ente } = useAlberatura();
  const [abilitato, setAbilitato] = useState(false);

  useEffect(() => {
    if (!utente) return;
    const controllo = new AbortController();
    statoAssistente(controllo.signal)
      .then((s) => setAbilitato(s.abilitato))
      .catch((e: unknown) => {
        if (controllo.signal.aborted) return;
        // Un 404 e' «non installato»; qualunque altro guasto non deve nascondere
        // il launcher in silenzio: lo si mostra, e l'errore si vede al primo invio.
        setAbilitato(!(e instanceof ErroreApi && e.stato === 404));
      });
    return () => controllo.abort();
  }, [utente]);

  if (!utente || !abilitato) return null;
  const chiave = `trasparenza:assistente:${utente.id}:${ente?.id ?? "-"}`;
  // La chiave come `key`: cambiando utente o ente il widget rinasce con la sua storia.
  return <AssistenteAttivo key={chiave} chiaveStoria={chiave} enteId={ente?.id ?? null} />;
}

function AssistenteAttivo({ chiaveStoria, enteId }: { chiaveStoria: string; enteId: number | null }) {
  const { tuttiINodi } = useAlberatura();
  const location = useLocation();
  const [parametri] = useSearchParams();
  const mobile = useMobile();

  const [storia] = useState(() => creaStoria(chiaveStoria, memoriaSessione()));
  const [voci, setVoci] = useState<Voce[]>(() => storia.leggi());
  const [launcherVisibile, setLauncherVisibile] = useState(false);
  const [aperto, setAperto] = useState(false);
  const [espanso, setEspanso] = useState(false);
  const [inArrivo, setInArrivo] = useState<string | null>(null);
  const [occupato, setOccupato] = useState(false);
  const [errore, setErrore] = useState<{ testo: string; azione: "riprova" | "ricomincia" | null } | null>(null);
  const [apertura, setApertura] = useState<AperturaAssistente | null>(null);
  const [suggerimenti, setSuggerimenti] = useState<string[]>(SUGGERIMENTI_GENERALI);
  const [nodoUltimaDomanda, setNodoUltimaDomanda] = useState<string | null>(null);

  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const ultimaDomanda = useRef<string | null>(null);
  const nodoSalutato = useRef<string | null>(null);
  // Cresce a ogni «Chiudi»: una risposta in volo appartenente a una
  // conversazione gia' azzerata non deve essere salvata nella nuova.
  const generazione = useRef(0);

  // Il nodo corrente: il parametro `nodo` di Pubblicazioni o di Documenti e
  // cartelle, se c'e' ed esiste.
  const idNodo = idNodoDallaRotta(location.pathname, parametri);
  const nodo: Nodo | null = idNodo ? (tuttiINodi.find((n) => n.id_nodo === idNodo) ?? null) : null;
  const chiaveNodo = nodo?.id_nodo ?? "-";

  useEffect(() => {
    const t = window.setTimeout(() => setLauncherVisibile(true), RITARDO_LAUNCHER_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Pannello aperto e nodo cambiato: divisore, saluto dalla scheda, domande di
  // quel nodo. E' una sincronizzazione con l'API, quindi un effetto.
  useEffect(() => {
    if (!aperto || nodoSalutato.current === chiaveNodo) return;
    nodoSalutato.current = chiaveNodo;
    if (!nodo) {
      const t = window.setTimeout(() => {
        setApertura(null);
        setSuggerimenti(SUGGERIMENTI_GENERALI);
      }, 0);
      return () => window.clearTimeout(t);
    }
    const controllo = new AbortController();
    aperturaAssistente(nodo.id_nodo, enteId, controllo.signal)
      .then((a) => {
        setApertura(a);
        setSuggerimenti(a.suggerimenti);
        storia.aggiungi({ tipo: "pagina", titolo: a.titolo });
        storia.aggiungi({ tipo: "assistente", testo: a.saluto, saluto: true });
        setVoci(storia.leggi());
      })
      .catch((e: unknown) => {
        if (controllo.signal.aborted) return;
        // Si ritentera' al prossimo cambio di nodo; intanto l'operatore vede
        // perche' manca il saluto.
        nodoSalutato.current = null;
        setApertura(null);
        setSuggerimenti(SUGGERIMENTI_GENERALI);
        setErrore({ testo: messaggioPerOperatore(e), azione: null });
      });
    return () => controllo.abort();
  }, [aperto, nodo, chiaveNodo, enteId, storia]);

  const riduci = () => {
    setAperto(false);
    setEspanso(false);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  };

  const chiudi = () => {
    generazione.current += 1;
    abortRef.current?.abort();
    storia.svuota();
    setVoci([]);
    setInArrivo(null);
    setErrore(null);
    nodoSalutato.current = null;
    riduci();
  };

  const apri = () => {
    setAperto(true);
    if (!mobile) window.setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Escape riduce, ma solo se il fuoco e' dentro il pannello: una modale
  // aperta sopra deve poter usare Escape per se'.
  useEffect(() => {
    if (!aperto) return;
    const suTasto = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const pannello = document.getElementById("assistente-pannello");
      if (pannello && pannello.contains(document.activeElement)) {
        setAperto(false);
        setEspanso(false);
        window.setTimeout(() => launcherRef.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", suTasto);
    return () => document.removeEventListener("keydown", suTasto);
  }, [aperto]);

  const invia = async (testo: string) => {
    if (occupato) return;
    setErrore(null);
    ultimaDomanda.current = testo;
    setNodoUltimaDomanda(chiaveNodo);
    storia.aggiungi({ tipo: "utente", testo });
    const vociCorrenti = storia.leggi();
    setVoci(vociCorrenti);
    setOccupato(true);
    setInArrivo(null);

    const controllo = new AbortController();
    abortRef.current = controllo;
    const miaGenerazione = generazione.current;

    // Rivelazione progressiva: il testo arrivato si mostra qualche carattere per
    // fotogramma, cosi' scorre invece di comparire a blocchi grandi quanto i
    // pacchetti di rete.
    let arrivato = "";
    let mostrato = 0;
    let fotogramma: number | null = null;
    const rivela = () => {
      const passo = prossimoPasso(arrivato.length - mostrato);
      if (passo > 0) {
        mostrato += passo;
        setInArrivo(arrivato.slice(0, mostrato));
      }
      fotogramma = mostrato < arrivato.length ? window.requestAnimationFrame(rivela) : null;
    };

    // Una risposta e' completa solo se il server ha mandato `fine`: uno stream
    // che si chiude prima (pod riavviato, proxy che taglia) non e' una risposta.
    let concluso = false;
    let troncata = false;
    const inviati = ultimiTurni(vociCorrenti, TURNI_STORIA);
    try {
      const risposta = await streamAssistente(
        { id_nodo: nodo?.id_nodo ?? null, ente: enteId, messaggi: inviati },
        controllo.signal,
      );
      for await (const evento of leggiEventiSse(risposta)) {
        if (evento.evento === "token") {
          const dati = evento.dati as { testo?: string } | null;
          arrivato += String(dati?.testo ?? "");
          if (fotogramma === null) fotogramma = window.requestAnimationFrame(rivela);
        } else if (evento.evento === "errore") {
          const dati = evento.dati as { dettaglio?: string } | null;
          throw new ErroreInterna(dati?.dettaglio ?? MESSAGGIO_ERRORE);
        } else if (evento.evento === "fine") {
          concluso = true;
          troncata = Boolean((evento.dati as { troncata?: boolean } | null)?.troncata);
        }
      }
      if (!concluso) throw new ErroreInterna(arrivato.trim() ? MESSAGGIO_INCOMPLETA : MESSAGGIO_ERRORE);
    } catch (e) {
      // Interrotta dall'operatore o da «Chiudi»: nessun errore da mostrare.
      if (controllo.signal.aborted) return;
      if (e instanceof ErroreFiltro) {
        const rimedio = rimedioAlBlocco(inviati, e.riferimento);
        setErrore({ testo: rimedio.testo, azione: rimedio.ricomincia ? "ricomincia" : null });
      } else {
        setErrore({ testo: messaggioPerOperatore(e), azione: "riprova" });
      }
    } finally {
      if (fotogramma !== null) window.cancelAnimationFrame(fotogramma);
      // Si salva solo se la conversazione e' ancora quella di partenza: dopo
      // «Chiudi» una risposta in volo non deve riapparire nella storia vuota.
      if (arrivato.trim() && generazione.current === miaGenerazione) {
        storia.aggiungi({ tipo: "assistente", testo: arrivato, troncata: !concluso || troncata });
        setVoci(storia.leggi());
      }
      setInArrivo(null);
      setOccupato(false);
      abortRef.current = null;
    }
  };

  const riprova = () => {
    if (ultimaDomanda.current) void invia(ultimaDomanda.current);
  };

  // Il divisore taglia la storia che va al modello (vedi `ultimiTurni`) senza
  // togliere all'operatore quello che ha gia' letto.
  const ricomincia = () => {
    if (occupato || !ultimaDomanda.current) return;
    storia.aggiungi({ tipo: "ricominciata" });
    void invia(ultimaDomanda.current);
  };

  const titolo = "Assistente alla trasparenza";
  const sottotitolo = apertura
    ? [apertura.titolo, apertura.aggiornamento ? `aggiornamento ${apertura.aggiornamento.toLowerCase()}` : ""]
        .filter(Boolean)
        .join(" · ")
    : (nodo?.denominazione ?? null);
  const mostraSuggerimenti = nodoUltimaDomanda !== chiaveNodo;

  return (
    <>
      {aperto ? (
        <Pannello
          titolo={titolo}
          sottotitolo={sottotitolo}
          espanso={espanso}
          mobile={mobile}
          onRiduci={riduci}
          onEspandi={() => setEspanso((v) => !v)}
          onChiudi={chiudi}
        >
          <Messaggi
            voci={voci}
            inArrivo={inArrivo}
            occupato={occupato}
            errore={errore?.testo ?? null}
            onRiprova={errore?.azione === "riprova" ? riprova : undefined}
            onRicomincia={errore?.azione === "ricomincia" ? ricomincia : undefined}
            suggerimenti={mostraSuggerimenti ? suggerimenti : []}
            onSuggerimento={(s) => void invia(s)}
            idNodoCorrente={nodo?.id_nodo ?? null}
          />
          <Input
            ref={inputRef}
            occupato={occupato}
            disabilitato={false}
            onInvia={(t) => void invia(t)}
            onInterrompi={() => abortRef.current?.abort()}
          />
        </Pannello>
      ) : null}
      {!(aperto && mobile) ? (
        <Launcher
          ref={launcherRef}
          aperto={aperto}
          visibile={launcherVisibile}
          nonLetto={!aperto && voci.some((v) => v.tipo === "assistente" && !v.saluto)}
          onClick={aperto ? riduci : apri}
        />
      ) : null}
    </>
  );
}
